import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Container,
  CircularProgress,
  Tab,
  Tabs,
  ThemeProvider,
  Typography,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { useQuery } from 'react-query';
import { useLocation } from 'react-router-dom';

import AccountSettings from './components/AccountSettings/AccountSettings';
import ChangePassword from './components/ChangePassword/ChangePassword';
import userProfileTheme from './userProfileTheme';
import { AvailableLanguagesType } from './types/AvailableLanguagesType';
import { CountryType } from './types/CountryType';
import ApiProvider from '../../../core/api/ApiProvider';
import Status from '../../../core/types/Status';
import { InfoMessageService, TranslationService, UserLanguageService } from '../../../core/services';
import { TenantAccountType } from '../../types/TenantAccountType';
import { ActionStatusType } from '../../../core/types/ActionStatusType';

type ModelType = {
  accountSettingsTabPath: string;
  accountSettingsTabVM: any;
  changePassVM: any;
  countries: CountryType[];
  changePasswordTabPath: string;
  isUserFirstLoginOrUnveryfiedAccount: boolean;
  isVisibleAccountSettingsTab: boolean;
  isVisibleChangePasswordTab: boolean;
  translations: any;
  tenantDebtors: TenantAccountType[];
};

enum TabTypeEnum {
  Account = 'account-settings',
  Password = 'change-password',
}

const UserProfile = () => {
  const [activeTab, setActiveTab] = useState<TabTypeEnum>(TabTypeEnum.Account);
  const location = useLocation();

  const model = useQuery<ActionStatusType<ModelType>>(
    ['model'],
    async () => {
      const path = '/UserProfileModule/GetModelData';
      return await ApiProvider.get<Status<ModelType>>(path);
    },
    {
      refetchOnWindowFocus: false,
      keepPreviousData: true,
      onError: () => {
        InfoMessageService.error(TranslationService.translate('GeneralErrorMsg'));
      },
    }
  );

  const availableLanguages = useQuery<
    ActionStatusType<{ languages: AvailableLanguagesType[]; selectedLanguage: string }>
  >(
    ['availableLanguages'],
    () => {
      return ApiProvider.post<
        Status<{ languages: AvailableLanguagesType[]; selectedLanguage: string }>
      >(`/UserProfileModule/GetAvailableLanguages`);
    },
    {
      enabled: !model.isLoading && !model.isFetching,
      refetchOnWindowFocus: false,
      keepPreviousData: true,
      onError: () => {
        InfoMessageService.error(TranslationService.translate('GeneralErrorMsg'));
      },
    }
  );

  useEffect(() => {
    if (location.hash.toLocaleLowerCase() === '#profile-password') {
      setActiveTab(TabTypeEnum.Password);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const modelData = model.data?.data;
  const languagesData = availableLanguages.data?.data;

  const visibleTabs = useMemo(
    () => ({
      account: !!modelData?.isVisibleAccountSettingsTab,
      password: !!modelData?.isVisibleChangePasswordTab,
    }),
    [modelData?.isVisibleAccountSettingsTab, modelData?.isVisibleChangePasswordTab]
  );

  // If the currently-selected tab becomes invisible, switch to the other visible one.
  useEffect(() => {
    if (activeTab === TabTypeEnum.Account && !visibleTabs.account && visibleTabs.password) {
      setActiveTab(TabTypeEnum.Password);
    } else if (activeTab === TabTypeEnum.Password && !visibleTabs.password && visibleTabs.account) {
      setActiveTab(TabTypeEnum.Account);
    }
  }, [activeTab, visibleTabs.account, visibleTabs.password]);

  const isInitiallyLoading = model.isLoading && !modelData;

  // Pin the picker to the user's language so it doesn't inherit moment's mutated global locale.
  const pickerLocale = (UserLanguageService.getLanguageCode() || 'en').toLowerCase();

  return (
    <ThemeProvider theme={userProfileTheme}>
      <LocalizationProvider dateAdapter={AdapterMoment} adapterLocale={pickerLocale}>
        <Box
          sx={{ backgroundColor: 'background.default', minHeight: '100%', py: { xs: 2, sm: 4 } }}
        >
          <Container maxWidth="md" sx={{ px: { xs: 2, sm: 3 } }}>
            <Typography
              variant="h5"
              component="h1"
              sx={{ fontWeight: 700, mb: 2, color: 'text.primary' }}
            >
              {TranslationService.translate('Profile')}
            </Typography>

            {isInitiallyLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                {(visibleTabs.account || visibleTabs.password) && (
                  <Box
                    sx={{
                      borderBottom: 1,
                      borderColor: 'divider',
                      mb: 2,
                      backgroundColor: 'background.paper',
                      borderRadius: 2,
                      px: { xs: 1, sm: 2 },
                    }}
                  >
                    <Tabs
                      value={activeTab}
                      onChange={(_e, value: TabTypeEnum) => setActiveTab(value)}
                      variant="scrollable"
                      scrollButtons="auto"
                      allowScrollButtonsMobile
                      aria-label={TranslationService.translate('Profile')}
                    >
                      {visibleTabs.account && (
                        <Tab
                          value={TabTypeEnum.Account}
                          label={`${TranslationService.translate(
                            'Account'
                          )} ${TranslationService.translate('Settings')}`}
                        />
                      )}
                      {visibleTabs.password && (
                        <Tab
                          value={TabTypeEnum.Password}
                          label={`${TranslationService.translate(
                            'Change'
                          )} ${TranslationService.translate('Password')}`}
                        />
                      )}
                    </Tabs>
                  </Box>
                )}

                {visibleTabs.account && activeTab === TabTypeEnum.Account && modelData && (
                  <AccountSettings
                    isUserFirstLoginOrUnveryfiedAccount={
                      modelData.isUserFirstLoginOrUnveryfiedAccount
                    }
                    selectedLanguage={languagesData?.selectedLanguage}
                    availableLanguages={languagesData?.languages}
                    accountSettingsTabVM={modelData.accountSettingsTabVM}
                    countries={modelData.countries}
                    tenantDebtors={modelData.tenantDebtors}
                    isLoading={model.isLoading || model.isFetching}
                  />
                )}

                {visibleTabs.password && activeTab === TabTypeEnum.Password && modelData && (
                  <ChangePassword changePassVM={modelData.changePassVM} />
                )}
              </>
            )}
          </Container>
        </Box>
      </LocalizationProvider>
    </ThemeProvider>
  );
};

export default UserProfile;
